import React, { useEffect, useState } from 'react';
import {
  activeRideBottomSheetAPI,
  handleApiError,
  maintenanceAPI,
  type MaintenanceState,
  webAccountsAPI,
  rideChatAPI,
} from '../services/api';
import './Dashboard.css';
import './Maintenance.css';

const SHEET_WARNING = 'Изключва долния панел на активното пътуване за всички приложения';

// The D2 kill switch. It has its own load and save, so a failure here
// never blocks the maintenance toggle above it.
const ActiveRideSheetSwitch: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Without a loaded value the page cannot tell which way the toggle goes.
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await activeRideBottomSheetAPI.getStatus();
        setEnabled(data.enabled);
      } catch (err) {
        setError(handleApiError(err));
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async () => {
    const next = !enabled;
    if (!next && !window.confirm(`${SHEET_WARNING}. Продължи?`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data } = await activeRideBottomSheetAPI.setStatus({ enabled: next });
      setEnabled(data.enabled);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="maintenance-messages maintenance-sheet-switch">
      <h3>Долен панел на активното пътуване</h3>
      <p className="maintenance-hint">
        <i className="fas fa-exclamation-triangle"></i> {SHEET_WARNING}.
      </p>

      {error && (
        <div className="maintenance-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      )}

      <div className={`maintenance-status ${enabled ? 'off' : 'on'}`}>
        <div className="maintenance-status-icon">
          <i className={enabled ? 'fas fa-check-circle' : 'fas fa-power-off'}></i>
        </div>
        <div className="maintenance-status-text">
          <div className="maintenance-status-title">
            {loading
              ? 'Зареждане…'
              : loadFailed
                ? 'Състоянието не е известно'
                : enabled
                  ? 'Панелът е включен'
                  : 'Панелът е ИЗКЛЮЧЕН'}
          </div>
          {!loading && !loadFailed && (
            <div className="maintenance-status-sub">
              {enabled
                ? 'Приложенията показват долния панел с картата.'
                : 'Приложенията показват стария изглед без карта.'}
            </div>
          )}
        </div>
        <button
          className={`maintenance-toggle ${enabled ? 'danger' : 'primary'}`}
          onClick={handleToggle}
          disabled={loading || saving || loadFailed}
        >
          {saving ? 'Запазване…' : enabled ? 'Изключи панела' : 'Включи панела'}
        </button>
      </div>
    </div>
  );
};

const WEB_ACCOUNTS_WARNING = 'Показва вход и профили на vozyme.eu за всички посетители';

// The web accounts kill switch. Off by default. Turning it on is the risky
// direction, so that one asks first. Own load and save, as the sheet switch.
const WebAccountsSwitch: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await webAccountsAPI.getStatus();
        setEnabled(data.enabled);
      } catch (err) {
        setError(handleApiError(err));
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async () => {
    const next = !enabled;
    if (next && !window.confirm(`${WEB_ACCOUNTS_WARNING}. Продължи?`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data } = await webAccountsAPI.setStatus({ enabled: next });
      setEnabled(data.enabled);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="maintenance-messages maintenance-sheet-switch">
      <h3>Профили в уеб приложението (vozyme.eu)</h3>
      <p className="maintenance-hint">
        <i className="fas fa-exclamation-triangle"></i> {WEB_ACCOUNTS_WARNING}.
      </p>

      {error && (
        <div className="maintenance-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      )}

      <div className={`maintenance-status ${enabled ? 'on' : 'off'}`}>
        <div className="maintenance-status-icon">
          <i className={enabled ? 'fas fa-user-check' : 'fas fa-power-off'}></i>
        </div>
        <div className="maintenance-status-text">
          <div className="maintenance-status-title">
            {loading
              ? 'Зареждане…'
              : loadFailed
                ? 'Състоянието не е известно'
                : enabled
                  ? 'Профилите са ВКЛЮЧЕНИ'
                  : 'Профилите са изключени'}
          </div>
          {!loading && !loadFailed && (
            <div className="maintenance-status-sub">
              {enabled
                ? 'vozyme.eu показва вход и профили.'
                : 'vozyme.eu скрива вход и профили.'}
            </div>
          )}
        </div>
        <button
          className={`maintenance-toggle ${enabled ? 'danger' : 'primary'}`}
          onClick={handleToggle}
          disabled={loading || saving || loadFailed}
        >
          {saving ? 'Запазване…' : enabled ? 'Изключи профилите' : 'Включи профилите'}
        </button>
      </div>
    </div>
  );
};

const RIDE_CHAT_WARNING = 'Включва чата към всяко пътуване за всички потребители';

// The ride chat switch. Follows the server env var until used. Turning it on is the risky
// direction, so that one asks first. Own load and save, as the sheet switch.
const RideChatSwitch: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await rideChatAPI.getStatus();
        setEnabled(data.enabled);
      } catch (err) {
        setError(handleApiError(err));
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleToggle = async () => {
    const next = !enabled;
    if (next && !window.confirm(`${RIDE_CHAT_WARNING}. Продължи?`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data } = await rideChatAPI.setStatus({ enabled: next });
      setEnabled(data.enabled);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="maintenance-messages maintenance-sheet-switch">
      <h3>Чат към пътуване</h3>
      <p className="maintenance-hint">
        <i className="fas fa-exclamation-triangle"></i> {RIDE_CHAT_WARNING}.
      </p>

      {error && (
        <div className="maintenance-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      )}

      <div className={`maintenance-status ${enabled ? 'on' : 'off'}`}>
        <div className="maintenance-status-icon">
          <i className={enabled ? 'fas fa-comments' : 'fas fa-power-off'}></i>
        </div>
        <div className="maintenance-status-text">
          <div className="maintenance-status-title">
            {loading
              ? 'Зареждане…'
              : loadFailed
                ? 'Състоянието не е известно'
                : enabled
                  ? 'Чатът е ВКЛЮЧЕН'
                  : 'Чатът е изключен'}
          </div>
          {!loading && !loadFailed && (
            <div className="maintenance-status-sub">
              {enabled
                ? 'Всяка резервация отваря чат стая.'
                : 'Резервациите не отварят чат стаи.'}
            </div>
          )}
        </div>
        <button
          className={`maintenance-toggle ${enabled ? 'danger' : 'primary'}`}
          onClick={handleToggle}
          disabled={loading || saving || loadFailed}
        >
          {saving ? 'Запазване…' : enabled ? 'Изключи чата' : 'Включи чата'}
        </button>
      </div>
    </div>
  );
};

const Maintenance: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [messageBg, setMessageBg] = useState<string>('');
  const [messageEn, setMessageEn] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await maintenanceAPI.getStatus();
      setEnabled(data.enabled);
      setMessageBg(data.message_bg);
      setMessageEn(data.message_en);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (next: MaintenanceState) => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await maintenanceAPI.setStatus(next);
      setEnabled(data.enabled);
      setMessageBg(data.message_bg);
      setMessageEn(data.message_en);
      setSavedAt(new Date());
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    save({ enabled: !enabled, message_bg: messageBg, message_en: messageEn });
  };

  const handleSaveMessages = () => {
    save({ enabled, message_bg: messageBg, message_en: messageEn });
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Режим на поддръжка</h2>
          <p className="last-updated">
            При активиране всички мобилни клиенти показват екран „В поддръжка“ при следващото изтегляне на конфигурацията.
          </p>
        </div>
      </div>

      {error && (
        <div className="maintenance-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      )}

      <div className={`maintenance-status ${enabled ? 'on' : 'off'}`}>
        <div className="maintenance-status-icon">
          <i className={enabled ? 'fas fa-tools' : 'fas fa-check-circle'}></i>
        </div>
        <div className="maintenance-status-text">
          <div className="maintenance-status-title">
            {loading ? 'Зареждане…' : enabled ? 'Режимът е АКТИВЕН' : 'Режимът е изключен'}
          </div>
          <div className="maintenance-status-sub">
            {enabled
              ? 'Потребителите виждат блокиращ екран в приложението.'
              : 'Приложението работи нормално.'}
          </div>
        </div>
        <button
          className={`maintenance-toggle ${enabled ? 'danger' : 'primary'}`}
          onClick={handleToggle}
          disabled={loading || saving}
        >
          {saving
            ? 'Запазване…'
            : enabled
              ? 'Изключи режим'
              : 'Активирай режим'}
        </button>
      </div>

      <div className="maintenance-messages">
        <h3>Съобщения за потребителите</h3>
        <p className="maintenance-hint">
          Тези текстове се показват на потребителите когато режимът е активен. Ако оставите празно, ще се ползва преводът по подразбиране.
        </p>

        <label className="maintenance-field">
          <span>Български</span>
          <textarea
            value={messageBg}
            onChange={(e) => setMessageBg(e.target.value)}
            placeholder="Например: Поддържаме сървъра. Опитай отново след 10 минути."
            maxLength={500}
            disabled={loading || saving}
            rows={3}
          />
        </label>

        <label className="maintenance-field">
          <span>English</span>
          <textarea
            value={messageEn}
            onChange={(e) => setMessageEn(e.target.value)}
            placeholder="e.g. We're performing maintenance. Please try again in 10 minutes."
            maxLength={500}
            disabled={loading || saving}
            rows={3}
          />
        </label>

        <div className="maintenance-actions">
          <button
            className="btn btn-primary"
            onClick={handleSaveMessages}
            disabled={loading || saving}
          >
            {saving ? 'Запазване…' : 'Запази съобщенията'}
          </button>
          {savedAt && (
            <span className="maintenance-saved">
              <i className="fas fa-check"></i>
              Запазено в {savedAt.toLocaleTimeString('bg-BG')}
            </span>
          )}
        </div>
      </div>

      <ActiveRideSheetSwitch />
      <WebAccountsSwitch />
      <RideChatSwitch />
    </div>
  );
};

export default Maintenance;
